<template>
  <div class="datarequest-page">
    <section class="datarequest-shell">
      <div class="datarequest-hero">
        <div class="hero-copy">
          <nav class="datarequest-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span>Data Requests</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">All Requests</span>
          </nav>

          <p class="hero-kicker">Request Review Desk</p>
          <h1>Review inbound dataset access requests in a more premium workspace</h1>
          <p class="hero-description">
            Monitor pending approvals, surface request context quickly, and review each request with
            linked dataset, project, and requester details in one polished view.
          </p>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Requests in view</span>
            <strong class="metric-value">{{ datarequests.length }}</strong>
            <span class="metric-meta">Enriched records currently loaded</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Pending</span>
            <strong class="metric-value">{{ pendingCount }}</strong>
            <span class="metric-meta">Awaiting administrative action</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Approved</span>
            <strong class="metric-value">{{ approvedCount }}</strong>
            <span class="metric-meta">Cleared dataset requests</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="status-switcher" role="tablist" aria-label="Data request status filters">
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isPending }"
            @click="changePend"
          >
            Pending Requests
            <span class="pill-count">{{ pendingCount }}</span>
          </button>
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isApproved }"
            @click="changeApprov"
          >
            Approved Requests
            <span class="pill-count">{{ approvedCount }}</span>
          </button>
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isAll }"
            @click="changeAll"
          >
            All Requests
            <span class="pill-count">{{ allRequests.length }}</span>
          </button>
        </div>

        <label class="search-shell" for="request-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            id="request-search"
            v-model.trim="search"
            type="search"
            placeholder="Search by title, dataset, project, or requester"
          />
        </label>
      </section>

      <div v-if="loading" class="loading-state">
        <b-spinner />
        <p>Compiling data request records...</p>
      </div>

      <template v-else>
        <div class="results-header">
          <div>
            <p class="results-kicker">Request queue</p>
            <h2>{{ currentViewTitle }}</h2>
          </div>
          <div class="results-meta">
            <span>{{ computedData.length }} result{{ computedData.length === 1 ? '' : 's' }}</span>
            <span class="results-divider"></span>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
          </div>
        </div>

        <div v-if="paginatedRequests.length" class="request-list">
          <article
            v-for="(datarequest, index) in paginatedRequests"
            :key="datarequest.id || index"
            class="request-card"
          >
            <div class="card-glow"></div>

            <div class="request-card__top">
              <div>
                <span class="request-index">Request {{ padIndex(index) }}</span>
                <h3>{{ datarequest.title || 'Untitled request' }}</h3>
              </div>

              <span class="status-badge" :class="statusBadgeClass(datarequest.status)">
                {{ datarequest.status || 'Pending' }}
              </span>
            </div>

            <p class="request-summary">
              {{ datarequest.description || 'No additional request description was provided.' }}
            </p>

            <div class="request-detail-grid">
              <div class="detail-card">
                <span class="detail-label">Requested dataset</span>
                <strong>{{ datarequest.datasetTitle || 'Dataset not resolved' }}</strong>
              </div>
              <div class="detail-card">
                <span class="detail-label">Related project</span>
                <strong>{{ datarequest.projectTitle || 'Project not resolved' }}</strong>
              </div>
              <div class="detail-card">
                <span class="detail-label">Submitted</span>
                <strong>{{ formatDate(datarequest.created_at) }}</strong>
              </div>
              <div class="detail-card">
                <span class="detail-label">Requester</span>
                <strong>{{ getRequesterName(datarequest) }}</strong>
              </div>
              <div class="detail-card">
                <span class="detail-label">Email</span>
                <strong>{{ getRequesterEmail(datarequest) }}</strong>
              </div>
              <div class="detail-card detail-card--wide">
                <span class="detail-label">Project context</span>
                <strong>{{
                  datarequest.projectDescription || 'No linked project description.'
                }}</strong>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="empty-state">
          <img src="../../../assets/no-data.svg" alt="No data requests available" />
          <h3>No data requests available</h3>
          <p>There are no requests matching the current filter and search combination.</p>
        </div>

        <div v-if="computedData.length" class="pagination-shell">
          <div class="pagination-copy">
            <p>Rows per page</p>
            <select id="request-page-size" v-model="perPage">
              <option value="3">3</option>
              <option value="5">5</option>
              <option value="10">10</option>
            </select>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="computedData.length"
            :per-page="perPage"
            pills
            align="right"
            aria-controls="request-list"
          />
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import instance from '@/plugin/axios';
import { mapActions } from 'vuex';

export default {
  data() {
    return {
      isAll: false,
      isApproved: false,
      isPending: true,
      currentPage: 1,
      loading: true,
      search: '',
      perPage: 5,
      allRequests: [],
      datarequests: [],
    };
  },

  computed: {
    approvedCount() {
      return this.datarequests.filter((request) => request?.status === 'Approved').length;
    },

    pendingCount() {
      return this.datarequests.filter((request) => request?.status === 'Pending').length;
    },

    currentViewTitle() {
      if (this.isApproved) return 'Approved data requests';
      if (this.isPending) return 'Pending data requests';
      return 'All data requests';
    },

    computedData() {
      const searchTerm = this.search.toLowerCase();

      return this.datarequests.filter((request) => {
        const haystack = [
          request.title,
          request.description,
          request.datasetTitle,
          request.projectTitle,
          request.projectDescription,
          this.getRequesterName(request),
          this.getRequesterEmail(request),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(searchTerm);
      });
    },

    paginatedRequests() {
      return this.computedData.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.computedData.length / this.perPage));
    },
  },

  watch: {
    perPage() {
      this.currentPage = 1;
    },

    search() {
      this.currentPage = 1;
    },
  },

  methods: {
    ...mapActions('admin', ['GET_DATAREQUEST']),

    async enrichRequests(requests) {
      return Promise.all(
        requests.map(async (request) => {
          try {
            const [userProject, dataset] = await Promise.all([
              request?.project ? instance.get(`/user_projects/${request.project}/`) : null,
              request?.dataset ? instance.get(`/datasets/${request.dataset}/`) : null,
            ]);

            return {
              datasetTitle: dataset?.data?.name || '',
              projectTitle: userProject?.data?.title || '',
              projectDescription: userProject?.data?.description || '',
              ...request,
            };
          } catch (error) {
            return {
              datasetTitle: '',
              projectTitle: '',
              projectDescription: '',
              ...request,
            };
          }
        })
      );
    },

    async fetchData() {
      this.loading = true;

      try {
        const response = await this.GET_DATAREQUEST();
        const results = response?.data?.results || [];
        const enrichedRequests = await this.enrichRequests(results);

        this.allRequests = enrichedRequests;
        this.datarequests = enrichedRequests.filter((request) => request?.status === 'Pending');
      } catch (error) {
        this.allRequests = [];
        this.datarequests = [];
      } finally {
        this.loading = false;
      }
    },

    changeAll() {
      this.isAll = true;
      this.isApproved = false;
      this.isPending = false;
      this.currentPage = 1;
      this.datarequests = [...this.allRequests];
    },

    changeApprov() {
      this.isAll = false;
      this.isApproved = true;
      this.isPending = false;
      this.currentPage = 1;
      this.datarequests = this.allRequests.filter((request) => request?.status === 'Approved');
    },

    changePend() {
      this.isAll = false;
      this.isApproved = false;
      this.isPending = true;
      this.currentPage = 1;
      this.datarequests = this.allRequests.filter((request) => request?.status === 'Pending');
    },

    formatDate(date) {
      if (!date) return 'Date unavailable';
      return moment(date).format('DD MMM YYYY');
    },

    getRequesterName(request) {
      const firstName = request?.user_details?.first_name || '';
      const lastName = request?.user_details?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();

      return fullName || 'Requester unavailable';
    },

    getRequesterEmail(request) {
      return request?.user_details?.username || 'Email unavailable';
    },

    statusBadgeClass(status) {
      return status === 'Approved' ? 'status-badge--approved' : 'status-badge--pending';
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },
  },

  async mounted() {
    await this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.datarequest-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(224, 230, 244, 0.92), transparent 28%),
    radial-gradient(circle at top right, rgba(54, 67, 112, 0.12), transparent 24%),
    linear-gradient(180deg, #f5f1e8 0%, #f5f7fb 46%, #edf1f8 100%);
}

.datarequest-shell {
  max-width: 1380px;
  margin: 0 auto;
}

.datarequest-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 1fr);
  gap: 1.5rem;
  padding: 1.75rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 248, 252, 0.9)), #fff;
  box-shadow: 0 24px 60px rgba(29, 39, 67, 0.1);
}

.datarequest-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6d7485;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #283255;
  font-weight: 600;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.65rem;
  color: #4257a2;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 14ch;
  margin-bottom: 0.85rem;
  color: #1d2645;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 4vw, 3.3rem);
  font-weight: 700;
  line-height: 1.04;
}

.hero-description {
  max-width: 62ch;
  margin-bottom: 0;
  color: #586075;
  font-size: 1rem;
  line-height: 1.75;
}

.hero-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 252, 0.9));
}

.metric-label {
  color: #657086;
  font-size: 0.88rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.metric-value {
  color: #1d2645;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-meta {
  color: #80899c;
  font-size: 0.92rem;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
}

.status-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.78rem 1rem;
  border: 1px solid rgba(57, 76, 142, 0.12);
  border-radius: 999px;
  background: rgba(247, 248, 252, 0.96);
  color: #475b8b;
  font-weight: 600;
  transition: all 0.2s ease;
}

.status-pill:hover,
.status-pill--active {
  background: linear-gradient(135deg, #34418a, #5a70c4);
  color: #fff;
  box-shadow: 0 16px 32px rgba(52, 65, 138, 0.2);
}

.pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: rgba(18, 31, 63, 0.08);
  font-size: 0.86rem;
}

.status-pill--active .pill-count {
  background: rgba(255, 255, 255, 0.18);
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: min(100%, 360px);
  margin-bottom: 0;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 18px;
  background: #fafbfe;
  color: #748099;
}

.search-shell input {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #273253;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 420px;
  margin-top: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.loading-state p,
.empty-state p {
  margin-bottom: 0;
  color: #707a8d;
}

.empty-state img {
  width: min(100%, 360px);
}

.results-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.5rem 0 1rem;
}

.results-header h2 {
  margin-bottom: 0;
  color: #1d2645;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.45rem, 2.6vw, 2rem);
  font-weight: 700;
}

.results-meta {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #6d788e;
  font-weight: 500;
}

.results-divider {
  width: 1px;
  height: 1rem;
  background: rgba(96, 106, 130, 0.25);
}

.request-list {
  display: grid;
  gap: 1rem;
}

.request-card {
  position: relative;
  overflow: hidden;
  padding: 1.45rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 26px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(246, 248, 252, 0.92)), #fff;
  box-shadow: 0 20px 40px rgba(27, 37, 67, 0.08);
}

.card-glow {
  position: absolute;
  top: -80px;
  right: -20px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(87, 105, 187, 0.12), transparent 68%);
  pointer-events: none;
}

.request-card__top,
.request-detail-grid {
  position: relative;
  z-index: 1;
}

.request-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.request-index {
  display: inline-block;
  margin-bottom: 0.5rem;
  color: #7d87a1;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.request-card__top h3 {
  margin-bottom: 0;
  color: #243052;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.35;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.status-badge--approved {
  background: rgba(39, 120, 74, 0.12);
  color: #21643f;
}

.status-badge--pending {
  background: rgba(185, 129, 29, 0.14);
  color: #8f6417;
}

.request-summary {
  position: relative;
  z-index: 1;
  margin: 1rem 0 1.2rem;
  color: #5b657b;
  line-height: 1.7;
}

.request-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.detail-card {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 18px;
  background: rgba(248, 249, 253, 0.92);
}

.detail-card--wide {
  grid-column: span 3;
}

.detail-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #788399;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.detail-card strong {
  color: #243052;
  font-size: 0.98rem;
  line-height: 1.5;
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(34, 47, 90, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.86);
}

.pagination-copy {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.pagination-copy p {
  margin-bottom: 0;
  color: #6f7a90;
}

.pagination-copy select {
  min-width: 72px;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(34, 47, 90, 0.12);
  border-radius: 14px;
  background: #fff;
}

::v-deep .pagination .page-item .page-link {
  border: 0;
  border-radius: 999px;
  margin: 0 0.2rem;
  color: #30408a;
  background: #edf1fb;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #34418a, #5a70c4);
  color: #fff;
  box-shadow: 0 10px 20px rgba(52, 65, 138, 0.18);
}

@media (max-width: 991px) {
  .datarequest-page {
    padding-top: 0;
  }

  .datarequest-shell {
    margin-top: 0;
  }

  .datarequest-hero,
  .request-detail-grid,
  .detail-card--wide {
    grid-template-columns: 1fr;
  }

  .detail-card--wide {
    grid-column: auto;
  }

  .toolbar,
  .results-header,
  .pagination-shell {
    flex-direction: column;
    align-items: stretch;
  }

  .results-meta {
    justify-content: space-between;
  }

  .search-shell {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .datarequest-page {
    padding: 0;
  }

  .datarequest-shell {
    margin-top: 0;
  }

  .datarequest-hero,
  .toolbar,
  .request-card,
  .pagination-shell,
  .loading-state,
  .empty-state {
    border-radius: 22px;
  }

  .request-card__top {
    flex-direction: column;
  }

  .datarequest-hero,
  .toolbar,
  .pagination-shell {
    padding: 1.1rem;
  }

  .hero-copy h1 {
    max-width: none;
    font-size: clamp(1.9rem, 8vw, 2.75rem);
  }
}

@media (max-width: 600px) {
  .datarequest-page {
    padding-top: 0;
  }

  .datarequest-shell {
    margin-top: 0;
  }
}
</style>
